import React, {useContext, useEffect} from "react";
import {Box, Button, Grid, TextField, Typography, useTheme} from "@mui/material";
import {Cancel, CheckCircle} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {UseQueryResult} from "@tanstack/react-query/src/types";
import {useRecoilState, useRecoilValue} from "recoil";
import {AxiosError} from "axios";
import {
	bookingDataValueState,
	checkDriverValidityValueState,
	customerDataValueState,
	recoveryRaModeState,
	stepState
} from "../state";
import {raCheck, retriveDraftRA} from '../services/http.services';
import {SET_SNACKBAR} from "../../common/utils";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../util/component/CustomLoader";
import {ICustomerData} from "../../models/models";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {fillPersonResponse} from "../../utils";
import _ from "underscore";

const CheckDriversValidity = () => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);

	const params = useParams(); // params.id

	// Use Context
	const { dispatchGlobalData } = useContext(GlobalContext);

	const [step, setStep] = useRecoilState(stepState);
	const [checkDriverValidityValue, setCheckDriverValidityValue] = useRecoilState(checkDriverValidityValueState);
	const [customerDataValue, setCustomerDataValue] = useRecoilState(customerDataValueState);
	const bookingDataValue = useRecoilValue(bookingDataValueState);
	const recoveryMode = useRecoilValue(recoveryRaModeState);

	const checkDriversPayload = {} as any;
	const addDriverKey = ['firstDriver', 'driver'];

	const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(message),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	// Manage Back & Next step actions
	const handleBackStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		if (step.active > 0) {
			setStep({
				active: step.active - 1,
				completed: step.completed,
				numSteps: step.numSteps,
			});

			setCheckDriverValidityValue({});
		}
	};

	const gotoNextStep = () => {
		const completed = step.completed.concat();
		if ((!!bookingDataValue.bookingDetail?.flagSubstitution || recoveryMode) && step.active < (step.numSteps - 2)) {
			completed[step.active + 1] = true;
			completed[step.active + 2] = true;
			setStep({
				active: step.active + 2,
				completed,
				numSteps: step.numSteps
			});
		} else if (step.active < (step.numSteps - 1)) {
			completed[step.active + 1] = true;
			setStep({
				active: step.active + 1,
				completed,
				numSteps: step.numSteps
			});
		}
	}

	const checkRecoveryRaDrivers = (drivers: any) => {
		for (const [key, driver] of Object.entries(drivers)) {
			if (!(driver as any).checkBlacklist) {
				viewErrorMessage('checkDriverValidity.message.warning.recoveryRaCheck.watchList', snackbarTypes.warning);
				break;
			}

			if (!(driver as any).checkMcTc) {
				viewErrorMessage('checkDriverValidity.message.warning.recoveryRaCheck.mctc', snackbarTypes.warning);
				break;
			}
		}
	}

	const retriveDraftRaQuery: UseQueryResult = useQuery(['retriveDraftRA', {id: params.id}], () => retriveDraftRA(params.id), {
		enabled: false,
		onSuccess: (response: any) => {
			let updatedCustomerData: any = {
				mainDriver: fillPersonResponse(response.raOwner),
				additionalDrivers: []
			};

			if (!!response.firstDriver) {
				updatedCustomerData.additionalDrivers.push(fillPersonResponse(response.firstDriver))
			}

			if (!!response.driver) {
				updatedCustomerData.additionalDrivers.push(fillPersonResponse(response.driver))
			}

			setCustomerDataValue(updatedCustomerData);

			gotoNextStep();
		},
		onError: (error: AxiosError) => {
			dispatchGlobalData({type: setSnackbar,
				payload: {
					snackbar: {
						message: t('translation:error.tryLater'),
						type: snackbarTypes.error,
						show: true,
						autoClose: true,
					}
				}
			});
		}
	})

	const handleNextStep = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		// check driver validity
		if (recoveryMode) {
			gotoNextStep();
		}
		else if (!!checkDriverValidityValue['raOwner']?.checkMcTc && !!checkDriverValidityValue['raOwner']?.checkBlacklist) {
			//update customer data after check
			if (
				(checkDriverValidityValue['firstDriver'] !== undefined && (!checkDriverValidityValue['firstDriver'].checkMcTc || !checkDriverValidityValue['firstDriver'].checkBlacklist)) ||
				(checkDriverValidityValue['driver'] !== undefined && (!checkDriverValidityValue['driver'].checkMcTc || !checkDriverValidityValue['driver'].checkBlacklist))
			) {
				//update customerData
				retriveDraftRaQuery.refetch();
			}
			else {
				gotoNextStep();
			}
		}
		else {
			dispatchGlobalData({
				type: 'SET_SNACKBAR',
				payload: {
					snackbar: {
						message: t('translation:error.tryLater'),
						type: snackbarTypes.error,
						show: true,
						autoClose: true,
					}
				}
			});
		}
	}

	/**
	 * Refetch wrest /network/ra/check
	 */
	const requestCheck = () => {
		checkDriversPayload.raOwner = buildCheckPayload(customerDataValue.mainDriver);
		['firstDriver', 'driver']
			.map((elem: string, ind: number) => {
				if (!!customerDataValue.additionalDrivers[ind]) {
					checkDriversPayload[elem] = buildCheckPayload(customerDataValue.additionalDrivers[ind]);
				}
				return elem;
			});
		checkDriversPayload.idRentalAgreement = bookingDataValue.idRentalAgreement;
		checkDriversPayload.recoveryMode = bookingDataValue.recoveryMode;
		checkDriversPayload.bookingDetail= {pickUpLocation: bookingDataValue.bookingDetail.pickUpLocation};
		checkDriversPayload.idTenant = bookingDataValue.idTenant;
		checkDriversPayload.dealer = bookingDataValue.dealer;
		refetch();
	}

	const handleRequestCheck = (e: React.SyntheticEvent) => {
		e.stopPropagation();

		requestCheck();
	}

	const onSuccessCheckDrivers = (response: any) => {
		const updatedCheckDriverValidityValue: any = {raOwner: response.raOwner};

		customerDataValue.additionalDrivers
			.map((driver: any, index: number) => {
				updatedCheckDriverValidityValue[addDriverKey[index]] = response[addDriverKey[index]];
				return driver;
			});

		if (recoveryMode) {
			checkRecoveryRaDrivers(updatedCheckDriverValidityValue);
		}

		setCheckDriverValidityValue(updatedCheckDriverValidityValue);
	};

	const onErrorCheckDrivers = (error: any) => {
		dispatchGlobalData(SET_SNACKBAR.ERROR);
	}

	function buildCheckPayload(driver: ICustomerData): any {
		return {
				firstName: driver.name,
				lastName: driver.surname,
				birthDate: !!driver.birthDate ? (typeof driver.birthDate === 'string' ? driver.birthDate : driver.birthDate.toISOString()) : null,
				countryResidence: driver.residenceCountry,
				cityResidence: {code: driver.residenceCity, description: driver.residenceCity},
				fiscalCode: driver.fiscalCode,
				customerType: driver.customerType,
				customerId: driver.customerID,
				cbId: driver.cbId,
				drivingLicenseNumber: driver.drivingLicenseNumber,
				issuingAuthority: driver.issuingAuthority,
				dateIssue: !!driver.issuingDate ? ( typeof driver.issuingDate === "string" ? driver.issuingDate : driver.issuingDate.toISOString() ) : null,
				expireDate: !!driver.expireDate ? ( typeof driver.expireDate === "string" ? driver.expireDate : driver.expireDate.toISOString() ) : null
		}
	}

	const {isFetching, refetch} = useQuery(['raCheck'], () => raCheck(checkDriversPayload), {
		enabled: false,
		onSuccess: (data) => onSuccessCheckDrivers(data),
		onError: (error) => onErrorCheckDrivers(error)
	})

	useEffect(() => {
		if (_.isEmpty(customerDataValue)) {
			return
		}

		if (checkDriverValidityValue['raOwner'] === undefined) {
			requestCheck();
		}
	}, [checkDriverValidityValue])

	if (_.isEmpty(customerDataValue)) {
		return <></>
	}

	return (
		<>
			<Grid container columnSpacing={2} sx={{maxWidth: '756px', mb: theme.spacing(4)}}>
				<Grid item xs={12}>
					<Typography variant={"h6"}>
						{t("checkDriverValidity.form.title.mainDriver")}
					</Typography>
				</Grid>
				<Grid item xs={5}>
					<TextField label={t("checkDriverValidity.form.label.name")}
							   value={customerDataValue.mainDriver.name}
							   fullWidth
							   disabled
					/>
				</Grid>
				<Grid item xs={5}>
					<TextField label={t("checkDriverValidity.form.label.surname")}
							   value={customerDataValue.mainDriver.surname}
							   fullWidth
							   disabled
					/>
				</Grid>
				<Grid item xs={2} sx={{display: "flex", alignItems: "flex-end"}}>
					{
						checkDriverValidityValue['raOwner'] !== undefined &&
						((checkDriverValidityValue['raOwner'].checkMcTc && checkDriverValidityValue['raOwner'].checkBlacklist) ?
							<CheckCircle sx={{color: theme.palette.success.main}} /> :
							<Cancel sx={{color: theme.palette.error.main}} />)
					}
				</Grid>

				{
					checkDriverValidityValue['raOwner'] !== undefined &&
					<Grid item xs={12}>
						{
							!checkDriverValidityValue?.['raOwner']?.checkMcTc &&
							<Box>
								<Typography sx={{color: theme.palette.error.main}}>
									{checkDriverValidityValue?.['raOwner']?.checkMcTcDesc}
								</Typography>
							</Box>
						}

						{
							!checkDriverValidityValue?.['raOwner']?.checkBlacklist &&
								<Box>
									<Typography sx={{color: theme.palette.error.main}}>
										{checkDriverValidityValue?.['raOwner']?.checkBlacklistDesc}
									</Typography>
								</Box>
						}
					</Grid>
				}
			</Grid>

			{
				customerDataValue.additionalDrivers
					.map((driver: any, index: number) =>
						<Grid container columnSpacing={2} sx={{maxWidth: '756px', mb: theme.spacing(4)}} key={index}>
							<Grid item xs={12}>
								<Typography variant="h6">
									{t('checkDriverValidity.form.title.additionalDriver')}
								</Typography>
							</Grid>
							<Grid item xs={5}>
								<TextField label={t('checkDriverValidity.form.label.name')}
										   value={driver.name}
										   fullWidth
										   disabled
								/>
							</Grid>
							<Grid item xs={5}>
								<TextField label={t('checkDriverValidity.form.label.surname')}
										   value={driver.surname}
										   fullWidth
										   disabled
								/>
							</Grid>

							<Grid item xs={2} sx={{display: "flex", alignItems: "flex-end"}}>
								{
									checkDriverValidityValue?.[addDriverKey[index]] !== undefined &&
									((checkDriverValidityValue?.[addDriverKey[index]].checkMcTc && checkDriverValidityValue?.[addDriverKey[index]].checkBlacklist) ?
										  <CheckCircle sx={{ color: theme.palette.success.main }} /> :
										  <Cancel sx={{ color: theme.palette.error.main }} />)
								}
							</Grid>

							{
								!recoveryMode &&
								checkDriverValidityValue[addDriverKey[index]] !== undefined &&
								<Grid item xs={12}>
									{
										!checkDriverValidityValue?.[addDriverKey[index]]?.checkMcTc &&
										<Box>
											<Typography sx={{color: theme.palette.error.main}}>
												{checkDriverValidityValue?.[addDriverKey[index]]?.checkMcTcDesc}
											</Typography>
										</Box>
									}

									{
										!checkDriverValidityValue?.[addDriverKey[index]]?.checkBlacklist &&
										<Box>
											<Typography sx={{color: theme.palette.error.main}}>
												{checkDriverValidityValue?.[addDriverKey[index]]?.checkBlacklistDesc}
											</Typography>
										</Box>
									}
								</Grid>
							}
						</Grid>
					)
			}

			<Box sx={{display: 'flex',  justifyContent: 'center', mt: theme.spacing(1)}}>
				<Button color={"accent"} onClick={handleRequestCheck}>
					{t('checkDriverValidity.form.button.requiestCheck')}
				</Button>
			</Box>

			<Box sx={{display: 'flex',  justifyContent: 'space-between', mt: theme.spacing(2)}}>
				{step.active === 0 ?
					<Box /> :
					<Button variant={'text'} color={"primary"}  onClick={handleBackStep}>
						{t('common.button.backStep')}
					</Button>
				}

				{step.active ===  (step.numSteps -1) ?
					<Box /> :
					<Button variant={'outlined'}
							color={"accent"}
							disabled={!recoveryMode && (!checkDriverValidityValue['raOwner']?.checkMcTc || !checkDriverValidityValue['raOwner']?.checkBlacklist)}
							onClick={handleNextStep}>
						{t('common.button.nextStep')}
					</Button>
				}
			</Box>

			<CustomLoader loading={isFetching} />
		</>
	)
}

export default CheckDriversValidity;